import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axios';
import toast from 'react-hot-toast';
import {
  ArrowRightIcon,
  BuildingOfficeIcon,
  MagnifyingGlassIcon,
  PhotoIcon,
  TrashIcon,
  PlusIcon,
  MinusIcon,
  CheckIcon,
  ShoppingBagIcon
} from '@heroicons/react/24/outline';

// Debounce hook for search
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

// ============================================
// Component: AgentNewReturnPage
// Purpose: Create a new return request - simplified form
// Flow: Search Company -> Select Products -> Submit
// ============================================

const RETURN_TYPES = [
  { value: 'changed_mind', label: 'לא נמכר / מלאי עודף' },
  { value: 'defective', label: 'מוצר פגום' },
  { value: 'wrong_item', label: 'מוצר שגוי' },
  { value: 'damaged_in_shipping', label: 'נזק במשלוח' },
  { value: 'other', label: 'אחר' }
];

const AgentNewReturnPage = () => {
  const navigate = useNavigate();
  
  // Company search
  const [companies, setCompanies] = useState([]);
  const [companySearch, setCompanySearch] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const debouncedCompanySearch = useDebounce(companySearch, 300);
  
  // Products - now fetching ALL products, not just purchased
  const [allProducts, setAllProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const debouncedProductSearch = useDebounce(productSearch, 300);
  
  // Selected items for return
  const [selectedItems, setSelectedItems] = useState([]);
  
  // Form
  const [returnType, setReturnType] = useState('changed_mind');
  const [returnReason, setReturnReason] = useState('');
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Fetch companies when search changes (debounced)
  useEffect(() => {
    fetchCompanies(debouncedCompanySearch);
  }, [debouncedCompanySearch]);

  // Fetch products when company is selected or product search changes
  useEffect(() => {
    if (selectedCompany) {
      fetchAllProducts(debouncedProductSearch);
    } else {
      setAllProducts([]);
      setSelectedItems([]);
    }
  }, [selectedCompany, debouncedProductSearch]);

  const fetchCompanies = async (searchQuery = '') => {
    try {
      setLoadingCompanies(true);
      const params = new URLSearchParams();
      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }
      const url = `/api/field-agent/companies${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await axiosInstance.get(url);
      // Extract company objects from the response
      const companyList = (response.data.companies || []).map(item => item.company || item);
      setCompanies(companyList);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('שגיאה בטעינת רשימת החברות');
    } finally {
      setLoadingCompanies(false);
    }
  };

  // Fetch ALL products - not limited to previous orders
  const fetchAllProducts = async (searchQuery = '') => {
    try {
      setLoadingProducts(true);
      const params = new URLSearchParams();
      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }
      params.append('limit', '100');
      const response = await axiosInstance.get(`/api/field-agent/returns/all-products?${params.toString()}`);
      setAllProducts(response.data.products || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('שגיאה בטעינת מוצרים');
    } finally {
      setLoadingProducts(false);
    }
  };

  // Companies are now filtered from the server, no need for local filtering
  // Products are also fetched from server with search - use allProducts directly
  const filteredProducts = allProducts;

  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    setCompanySearch('');
    setSelectedItems([]);
  };

  const handleProductToggle = (product) => {
    const exists = selectedItems.find(i => i.product_id === product.product_id);
    if (exists) {
      setSelectedItems(selectedItems.filter(i => i.product_id !== product.product_id));
    } else {
      setSelectedItems([
        ...selectedItems,
        {
          product_id: product.product_id,
          product: product,
          quantity: 1,
          unit_price: product.price || 0 // Use product price, allow editing
        }
      ]);
    }
  };

  const handleQuantityChange = (productId, newQuantity) => {
    setSelectedItems(selectedItems.map(item => {
      if (item.product_id === productId) {
        return {
          ...item,
          quantity: Math.max(1, newQuantity) // No max limit - allow any quantity
        };
      }
      return item;
    }));
  };

  // Allow editing price for legacy orders
  const handlePriceChange = (productId, newPrice) => {
    setSelectedItems(selectedItems.map(item => {
      if (item.product_id === productId) {
        return {
          ...item,
          unit_price: parseFloat(newPrice) || 0
        };
      }
      return item;
    }));
  };

  const handlePhotoAdd = (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > 10) {
      toast.error('ניתן להעלות עד 10 תמונות');
      return;
    }
    setPhotos([...photos, ...files]);
  };

  const handlePhotoRemove = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const VAT_RATE = 0.18; // 18% VAT

  const calculateSubtotal = () => {
    return selectedItems.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
  };

  const calculateVat = () => {
    return calculateSubtotal() * VAT_RATE;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateVat();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(amount || 0);
  };

  const handleSubmit = async () => {
    if (!selectedCompany) {
      toast.error('נא לבחור חברה');
      return;
    }
    if (selectedItems.length === 0) {
      toast.error('נא לבחור מוצרים להחזרה');
      return;
    }
    if (!returnReason.trim()) {
      toast.error('נא להזין סיבת החזרה');
      return;
    }

    try {
      setSubmitting(true);

      const formData = new FormData();
      formData.append('company_id', selectedCompany.id);
      formData.append('return_type', returnType);
      formData.append('return_reason', returnReason);
      formData.append('credit_to_balance', 'true'); // Flag to credit balance
      formData.append('items', JSON.stringify(selectedItems.map(item => ({
        product_id: item.product_id,
        quantity: item.quantity,
        unit_price: item.unit_price,
        total_credit: item.unit_price * item.quantity
      }))));

      photos.forEach(photo => {
        formData.append('photos', photo);
      });

      await axiosInstance.post('/api/field-agent/returns/quick', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(`בקשת החזרה נשלחה לאישור! סכום מבוקש: ${formatCurrency(calculateTotal())}`);
      navigate('/agent/returns');
    } catch (error) {
      console.error('Error creating return:', error);
      toast.error(error.response?.data?.message || 'שגיאה ביצירת החזרה');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      {/* Header */}
      <header className="bg-white shadow-sm p-4 border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/agent/returns')}
            className="text-gray-600 hover:text-gray-800 flex items-center gap-2"
          >
            <ArrowRightIcon className="h-5 w-5" />
            חזרה
          </button>
          <h1 className="text-xl font-bold text-gray-900">רישום החזרה</h1>
          <div className="w-16"></div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 pb-32">
        {/* Company Selection */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <label className="block text-lg font-semibold text-gray-900 mb-4">
            <BuildingOfficeIcon className="h-5 w-5 inline-block ml-2" />
            בחר חברה
          </label>
          
          {selectedCompany ? (
            <div className="flex items-center justify-between bg-primary-50 rounded-lg p-4 border-2 border-primary-500">
              <div className="flex items-center gap-3">
                <BuildingOfficeIcon className="h-8 w-8 text-primary-600" />
                <div>
                  <div className="font-bold text-primary-800">{selectedCompany.name}</div>
                  <div className="text-sm text-primary-600">ח.פ: {selectedCompany.business_number}</div>
                </div>
              </div>
              <button
                onClick={() => setSelectedCompany(null)}
                className="text-primary-600 hover:text-primary-800 text-sm font-medium"
              >
                שנה
              </button>
            </div>
          ) : (
            <>
              <div className="relative mb-3">
                <MagnifyingGlassIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={companySearch}
                  onChange={(e) => setCompanySearch(e.target.value)}
                  placeholder="חפש לפי שם חברה או ח.פ..."
                  className="w-full pr-10 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-lg"
                  autoFocus
                />
              </div>
              
              {loadingCompanies ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                </div>
              ) : (
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {companies.map((company) => (
                    <button
                      key={company.id}
                      onClick={() => handleCompanySelect(company)}
                      className="w-full bg-gray-50 rounded-lg p-3 text-right hover:bg-gray-100 transition-colors border border-gray-200 flex items-center gap-3"
                    >
                      <BuildingOfficeIcon className="h-6 w-6 text-gray-400" />
                      <div>
                        <div className="font-medium text-gray-900">{company.name}</div>
                        <div className="text-sm text-gray-500">ח.פ: {company.business_number}</div>
                      </div>
                    </button>
                  ))}
                  {companies.length === 0 && !loadingCompanies && (
                    <div className="text-center py-4 text-gray-500">
                      {companySearch ? 'לא נמצאו חברות התואמות את החיפוש' : 'אין חברות משויכות'}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Products Selection */}
        {selectedCompany && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <label className="block text-lg font-semibold text-gray-900 mb-4">
              <ShoppingBagIcon className="h-5 w-5 inline-block ml-2" />
              בחר מוצרים להחזרה
            </label>

            {/* Product Search */}
            <div className="relative mb-4">
              <MagnifyingGlassIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="חפש מוצר..."
                className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {loadingProducts ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              </div>
            ) : allProducts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <ShoppingBagIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p>{productSearch ? 'לא נמצאו מוצרים' : 'הקלד לחיפוש מוצר...'}</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {filteredProducts.map((product) => {
                  const isSelected = selectedItems.some(i => i.product_id === product.product_id);
                  const selectedItem = selectedItems.find(i => i.product_id === product.product_id);

                  return (
                    <div
                      key={product.product_id}
                      className={`rounded-lg border-2 p-4 transition-all ${
                        isSelected ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="flex gap-4">
                        {/* Checkbox */}
                        <div
                          onClick={() => handleProductToggle(product)}
                          className={`w-6 h-6 rounded border-2 flex items-center justify-center cursor-pointer flex-shrink-0 mt-1 ${
                            isSelected ? 'bg-primary-600 border-primary-600' : 'border-gray-300 hover:border-primary-400'
                          }`}
                        >
                          {isSelected && <CheckIcon className="h-4 w-4 text-white" />}
                        </div>

                        {/* Product Image */}
                        <img
                          src={product.image || '/placeholder-product.png'}
                          alt={product.name}
                          className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                        />

                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 truncate">{product.name}</div>
                          <div className="text-sm text-gray-500">מק"ט: {product.sku}</div>
                          <div className="flex flex-wrap gap-2 mt-1 text-xs">
                            {product.brand && (
                              <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                                {product.brand}
                              </span>
                            )}
                            <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded">
                              מחיר: {formatCurrency(product.price)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Quantity & Price Selector (when selected) */}
                      {isSelected && (
                        <div className="mt-4 pt-4 border-t border-primary-200 space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">כמות להחזרה:</span>
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => handleQuantityChange(product.product_id, selectedItem.quantity - 1)}
                                className="w-10 h-10 rounded-full bg-white border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                              >
                                <MinusIcon className="h-5 w-5" />
                              </button>
                              <input
                                type="number"
                                value={selectedItem.quantity}
                                onChange={(e) => handleQuantityChange(product.product_id, parseInt(e.target.value) || 1)}
                                className="w-16 text-center text-xl font-bold border rounded-lg py-1"
                                min="1"
                              />
                              <button
                                onClick={() => handleQuantityChange(product.product_id, selectedItem.quantity + 1)}
                                className="w-10 h-10 rounded-full bg-white border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                              >
                                <PlusIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">מחיר ליחידה (₪):</span>
                            <input
                              type="number"
                              value={selectedItem.unit_price}
                              onChange={(e) => handlePriceChange(product.product_id, e.target.value)}
                              className="w-24 text-center font-bold border rounded-lg py-1 px-2"
                              min="0"
                              step="0.01"
                            />
                          </div>
                          <div className="pt-2 border-t space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-600">סה"כ לפני מע"מ:</span>
                              <span className="font-medium text-gray-700">
                                {formatCurrency(selectedItem.unit_price * selectedItem.quantity)}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-600">מע"מ (18%):</span>
                              <span className="text-sm text-gray-600">
                                {formatCurrency(selectedItem.unit_price * selectedItem.quantity * VAT_RATE)}
                              </span>
                            </div>
                            <div className="flex items-center justify-between pt-1 border-t border-primary-200">
                              <span className="text-sm font-medium text-gray-700">סה"כ זיכוי:</span>
                              <span className="text-lg font-bold text-green-600">
                                {formatCurrency(selectedItem.unit_price * selectedItem.quantity * (1 + VAT_RATE))}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Return Details */}
        {selectedItems.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <label className="block text-lg font-semibold text-gray-900 mb-4">
              פרטי ההחזרה
            </label>

            {/* Return Type */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">סוג ההחזרה</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {RETURN_TYPES.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setReturnType(type.value)}
                    className={`p-3 rounded-lg border-2 text-sm transition-colors ${
                      returnType === type.value
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Return Reason */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                סיבת ההחזרה <span className="text-red-500">*</span>
              </label>
              <textarea
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="תאר את סיבת ההחזרה..."
              />
            </div>

            {/* Photos */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                תמונות (אופציונלי)
              </label>
              <div className="flex flex-wrap gap-2">
                {photos.map((photo, index) => (
                  <div key={index} className="relative">
                    <img
                      src={URL.createObjectURL(photo)}
                      alt={`Photo ${index + 1}`}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                    <button
                      onClick={() => handlePhotoRemove(index)}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                    >
                      <TrashIcon className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                {photos.length < 10 && (
                  <label className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary-400">
                    <PhotoIcon className="h-6 w-6 text-gray-400" />
                    <span className="text-xs text-gray-500 mt-1">הוסף</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoAdd}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Fixed Bottom Summary & Submit */}
      {selectedItems.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg p-4 z-30">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-3">
              <div>
                <span className="text-gray-600">{selectedItems.length} פריטים להחזרה</span>
              </div>
              <div className="text-left space-y-1">
                <div className="flex justify-between gap-8 text-sm text-gray-600">
                  <span>סה"כ לפני מע"מ:</span>
                  <span>{formatCurrency(calculateSubtotal())}</span>
                </div>
                <div className="flex justify-between gap-8 text-sm text-gray-600">
                  <span>מע"מ (18%):</span>
                  <span>{formatCurrency(calculateVat())}</span>
                </div>
                <div className="flex justify-between gap-8 pt-1 border-t border-gray-200">
                  <span className="text-sm font-medium text-gray-700">סה"כ זיכוי כולל מע"מ:</span>
                  <span className="text-xl font-bold text-green-600">{formatCurrency(calculateTotal())}</span>
                </div>
              </div>
            </div>
            <button
              onClick={handleSubmit}
              disabled={submitting || !returnReason.trim()}
              className="w-full bg-green-600 text-white py-4 rounded-xl font-bold text-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? 'מבצע החזרה...' : `רשום החזרה וזכה ${formatCurrency(calculateTotal())}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentNewReturnPage;
